import {
  ALL_DIALER_WIDGET_IDS,
  normalizeWidgetLayout,
  type DialerWidgetId,
  type DialerWidgetLayout,
} from './dialer-widgets-layout';
import {
  type DialerBelowMessagesContainerId,
  type DialerBelowMessagesLayout,
} from './dialer-below-messages-layout';

export type DialerHybridWidgetMode = 'two_columns' | 'three_columns';
export type DialerClassicTwoColumnsContainerId =
  | 'left'
  | 'below_left'
  | 'below_middle'
  | 'below_right'
  | 'right';
export type DialerClassicTwoColumnsLayout = Record<
  DialerClassicTwoColumnsContainerId,
  DialerWidgetId[]
>;

const TWO_COLUMNS_ORDER: DialerClassicTwoColumnsContainerId[] = [
  'left',
  'below_left',
  'below_middle',
];
const THREE_COLUMNS_ORDER: DialerClassicTwoColumnsContainerId[] = [
  'left',
  'below_left',
  'below_middle',
  'right',
];

const TWO_COLUMN_MESSAGE_AREA_IDS: DialerBelowMessagesContainerId[] = [
  'below_left',
  'below_middle',
];
const BELOW_MESSAGES_COLUMN_IDS: DialerBelowMessagesContainerId[] = [
  'below_left',
  'below_middle',
  'below_right',
];

function getHybridColumnOrder(mode: DialerHybridWidgetMode): DialerClassicTwoColumnsContainerId[] {
  return mode === 'three_columns' ? THREE_COLUMNS_ORDER : TWO_COLUMNS_ORDER;
}

function isWidgetId(value: string): value is DialerWidgetId {
  return ALL_DIALER_WIDGET_IDS.includes(value as DialerWidgetId);
}

function arrayMove<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  const copy = [...items];
  const [item] = copy.splice(fromIndex, 1);
  copy.splice(toIndex, 0, item);
  return copy;
}

function emptyHybridLayout(): DialerClassicTwoColumnsLayout {
  return {
    left: [],
    below_left: [],
    below_middle: [],
    below_right: [],
    right: [],
  };
}

function normalizeBelowMessagesColumns(
  belowMessagesLayout: Partial<Record<DialerBelowMessagesContainerId, string[]>> | null | undefined,
): Record<DialerBelowMessagesContainerId, DialerWidgetId[]> {
  const next: Record<DialerBelowMessagesContainerId, DialerWidgetId[]> = {
    below_left: [],
    below_middle: [],
    below_right: [],
  };
  const seen = new Set<DialerWidgetId>();

  for (const columnId of BELOW_MESSAGES_COLUMN_IDS) {
    const values = belowMessagesLayout?.[columnId] ?? [];
    for (const value of values) {
      if (!isWidgetId(value) || seen.has(value)) continue;
      seen.add(value);
      next[columnId].push(value);
    }
  }

  return next;
}

function normalizeOrderedWidgetIds(
  widgetLayout: Partial<Record<'left' | 'right', string[]>> | null | undefined,
  belowMessagesLayout:
    | Partial<Record<'below_left' | 'below_middle' | 'below_right', string[]>>
    | null
    | undefined,
): DialerWidgetId[] {
  const normalizedWidgetLayout = normalizeWidgetLayout(widgetLayout);
  const normalizedBelowMessagesLayout = normalizeBelowMessagesColumns(belowMessagesLayout);
  const seen = new Set<DialerWidgetId>();
  const ordered: DialerWidgetId[] = [];

  const append = (values: DialerWidgetId[]) => {
    for (const value of values) {
      if (seen.has(value)) continue;
      seen.add(value);
      ordered.push(value);
    }
  };

  append(normalizedWidgetLayout.left);
  append(normalizedBelowMessagesLayout.below_left);
  append(normalizedBelowMessagesLayout.below_middle);
  append(normalizedBelowMessagesLayout.below_right);
  append(normalizedWidgetLayout.right);

  for (const widgetId of ALL_DIALER_WIDGET_IDS) {
    if (seen.has(widgetId)) continue;
    ordered.push(widgetId);
  }

  return ordered;
}

function evenlySplitColumnSizes(total: number, columns: readonly string[]): number[] {
  const baseSize = Math.floor(total / columns.length);
  const remainder = total % columns.length;
  return columns.map((_, index) => baseSize + (index < remainder ? 1 : 0));
}

function flattenHybridLayout(
  layout: DialerClassicTwoColumnsLayout,
  mode: DialerHybridWidgetMode,
): DialerWidgetId[] {
  return getHybridColumnOrder(mode).flatMap((columnId) => layout[columnId]);
}

export function getMessageAreaColumnIds(
  mode: DialerHybridWidgetMode,
): DialerBelowMessagesContainerId[] {
  void mode;
  return TWO_COLUMN_MESSAGE_AREA_IDS;
}

export function normalizeClassicTwoColumnsLayout(
  widgetLayout: Partial<Record<'left' | 'right', string[]>> | null | undefined,
  belowMessagesLayout:
    | Partial<Record<'below_left' | 'below_middle' | 'below_right', string[]>>
    | null
    | undefined,
): DialerClassicTwoColumnsLayout {
  const normalizedWidgetLayout = normalizeWidgetLayout(widgetLayout);
  const normalizedBelowMessagesLayout = normalizeBelowMessagesColumns(belowMessagesLayout);
  const next = emptyHybridLayout();
  const seen = new Set<DialerWidgetId>();

  const appendColumn = (
    sourceValues: string[],
    targetColumnId: DialerClassicTwoColumnsContainerId,
  ) => {
    for (const value of sourceValues) {
      if (!isWidgetId(value) || seen.has(value)) continue;
      seen.add(value);
      next[targetColumnId].push(value);
    }
  };

  appendColumn(normalizedWidgetLayout.left, 'left');
  appendColumn(normalizedBelowMessagesLayout.below_left, 'below_left');
  appendColumn(normalizedBelowMessagesLayout.below_middle, 'below_middle');
  appendColumn(normalizedBelowMessagesLayout.below_right, 'below_right');
  appendColumn(normalizedWidgetLayout.right, 'right');

  for (const widgetId of ALL_DIALER_WIDGET_IDS) {
    if (seen.has(widgetId)) continue;
    next.right.push(widgetId);
  }

  return next;
}

export function rebalanceClassicTwoColumnsLayout(
  widgetLayout: Partial<Record<'left' | 'right', string[]>> | null | undefined,
  belowMessagesLayout:
    | Partial<Record<'below_left' | 'below_middle' | 'below_right', string[]>>
    | null
    | undefined,
  mode: DialerHybridWidgetMode,
): DialerClassicTwoColumnsLayout {
  const ordered = normalizeOrderedWidgetIds(widgetLayout, belowMessagesLayout);
  const activeColumns = getHybridColumnOrder(mode);
  const columnSizes = evenlySplitColumnSizes(ordered.length, activeColumns);
  const next = emptyHybridLayout();
  let cursor = 0;

  activeColumns.forEach((columnId, index) => {
    const takeCount = columnSizes[index];
    next[columnId] = ordered.slice(cursor, cursor + takeCount);
    cursor += takeCount;
  });

  return next;
}

export function resolveClassicTwoColumnsContainer(
  layout: DialerClassicTwoColumnsLayout,
  value: string | null | undefined,
  mode: DialerHybridWidgetMode,
): DialerClassicTwoColumnsContainerId | null {
  if (!value) return null;
  const activeColumns = getHybridColumnOrder(mode);
  if (activeColumns.includes(value as DialerClassicTwoColumnsContainerId)) {
    return value as DialerClassicTwoColumnsContainerId;
  }
  if (!isWidgetId(value)) return null;

  for (const columnId of activeColumns) {
    if (layout[columnId].includes(value)) return columnId;
  }

  return null;
}

export function moveClassicTwoColumnsWidgetInLayout(
  layout: DialerClassicTwoColumnsLayout,
  activeWidgetId: DialerWidgetId,
  overId: string | null | undefined,
  mode: DialerHybridWidgetMode,
): DialerClassicTwoColumnsLayout {
  const sourceContainer = resolveClassicTwoColumnsContainer(layout, activeWidgetId, mode);
  const targetContainer = resolveClassicTwoColumnsContainer(layout, overId, mode);

  if (!sourceContainer || !targetContainer) return layout;
  if (activeWidgetId === overId) return layout;

  if (sourceContainer === targetContainer) {
    const items = layout[sourceContainer];
    const oldIndex = items.indexOf(activeWidgetId);
    if (oldIndex < 0) return layout;

    const nextIndex =
      typeof overId === 'string' && isWidgetId(overId) ? items.indexOf(overId) : items.length - 1;
    if (nextIndex < 0 || nextIndex === oldIndex) return layout;

    return {
      ...layout,
      [sourceContainer]: arrayMove(items, oldIndex, nextIndex),
    };
  }

  const sourceItems = [...layout[sourceContainer]];
  const targetItems = [...layout[targetContainer]];
  const sourceIndex = sourceItems.indexOf(activeWidgetId);
  if (sourceIndex < 0) return layout;

  sourceItems.splice(sourceIndex, 1);

  const targetIndex =
    typeof overId === 'string' && isWidgetId(overId)
      ? Math.max(0, targetItems.indexOf(overId))
      : targetItems.length;
  targetItems.splice(targetIndex, 0, activeWidgetId);

  return {
    ...layout,
    [sourceContainer]: sourceItems,
    [targetContainer]: targetItems,
  };
}

export function splitClassicTwoColumnsLayout(layout: DialerClassicTwoColumnsLayout): {
  widgetLayout: DialerWidgetLayout;
  belowMessagesLayout: DialerBelowMessagesLayout;
} {
  const normalizedLayout = normalizeClassicTwoColumnsLayout(
    {
      left: layout.left,
      right: layout.right,
    },
    {
      below_left: layout.below_left,
      below_middle: layout.below_middle,
      below_right: layout.below_right,
    },
  );

  return {
    widgetLayout: {
      left: normalizedLayout.left,
      right: normalizedLayout.right,
    },
    belowMessagesLayout: {
      below_left: normalizedLayout.below_left,
      below_middle: normalizedLayout.below_middle,
      below_right: normalizedLayout.below_right,
    },
  };
}

export function flattenClassicTwoColumnsWidgetIds(
  layout: DialerClassicTwoColumnsLayout,
  mode: DialerHybridWidgetMode,
): DialerWidgetId[] {
  return flattenHybridLayout(layout, mode);
}
